import React, { useState, useEffect, useRef } from 'react';
import './AddressAutocomplete.css';

interface AddressSuggestion {
    id: string;
    place_name: string;
    center: [number, number];
}

interface AddressAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onValidationChange?: (isValid: boolean) => void; // Callback when validation state changes
    placeholder?: string;
    className?: string;
    required?: boolean;
    maxLength?: number;
    name?: string;
    id?: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
    value,
    onChange,
    onValidationChange,
    placeholder = "Enter address...",
    className = "",
    required = false,
    maxLength,
    name,
    id
}) => {
    const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isValidAddress, setIsValidAddress] = useState(false); // Track if address was selected from suggestions
    const debounceRef = useRef<NodeJS.Timeout>();
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Mapbox public token for client-side autocomplete
    const MAPBOX_API_KEY =
        process.env.REACT_APP_MAPBOX_MAP_API_KEY ||
        'pk.eyJ1IjoiaGFyb2xkbWMiLCJhIjoiY21kNnJuaHZ2MGQzMTJtcHN5MTl3NXA2eiJ9.9UBQJj05VQxrCdnhtN3ndQ';

    const fetchSuggestions = async (query: string) => {
        if (query.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setIsLoading(true);
        try {
            if (!MAPBOX_API_KEY) {
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }
            // Bias towards Florida (center of FL is approximately -82.4, 27.8)
            // proximity parameter biases results towards Florida but still shows addresses from anywhere in the US
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_API_KEY}&country=US&types=address,poi&limit=5&proximity=-82.4,27.8`
            );
            
            if (!response.ok) {
                throw new Error('Failed to fetch suggestions');
            }

            const data = await response.json();
            const formattedSuggestions: AddressSuggestion[] = data.features.map((feature: any) => ({
                id: feature.id,
                place_name: feature.place_name,
                center: feature.center
            }));

            setSuggestions(formattedSuggestions);
            setShowSuggestions(true);
            setSelectedIndex(-1);
        } catch (error) {
            console.error('Error fetching address suggestions:', error);
            setSuggestions([]);
            setShowSuggestions(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        onChange(newValue);
        
        // When user types manually, address is no longer valid (unless they select from suggestions)
        if (newValue.trim() === '') {
            setIsValidAddress(false);
            onValidationChange?.(false);
        } else {
            // Reset validation - user must select from suggestions
            setIsValidAddress(false);
            onValidationChange?.(false);
        }

        // Clear previous debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Debounce the API call
        debounceRef.current = setTimeout(() => {
            fetchSuggestions(newValue);
        }, 300);
    };

    const handleSuggestionClick = (suggestion: AddressSuggestion) => {
        onChange(suggestion.place_name);
        setSuggestions([]);
        setShowSuggestions(false);
        setSelectedIndex(-1);
        // Mark address as valid when selected from suggestions
        setIsValidAddress(true);
        onValidationChange?.(true);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showSuggestions || suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
                    handleSuggestionClick(suggestions[selectedIndex]);
                } else if (suggestions.length > 0) {
                    // If no selection but suggestions exist, select first one
                    handleSuggestionClick(suggestions[0]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setSelectedIndex(-1);
                break;
        }
    };

    const handleInputFocus = () => {
        if (suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    const handleInputBlur = (e: React.FocusEvent) => {
        // Delay hiding suggestions to allow for clicks
        setTimeout(() => {
            if (!suggestionsRef.current?.contains(document.activeElement)) {
                setShowSuggestions(false);
                setSelectedIndex(-1);
            }
        }, 150);
    };

    // Clean up debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <div className={`address-autocomplete ${className}`}>
            <input
                ref={inputRef}
                type="text"
                id={id}
                name={name}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                placeholder={placeholder}
                required={required}
                maxLength={maxLength}
                className="address-input"
                autoComplete="off"
            />

            {showSuggestions && suggestions.length > 0 && (
                <div ref={suggestionsRef} className="suggestions-dropdown">
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={suggestion.id}
                            className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                            onClick={() => handleSuggestionClick(suggestion)}
                            onMouseEnter={() => setSelectedIndex(index)}
                        >
                            <div className="suggestion-text">
                                {suggestion.place_name}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AddressAutocomplete;
